import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../../configs/firebase';
import { EAuthData } from '../Provider/EAuthProvider';
import { useAuthState } from 'react-firebase-hooks/auth';
import { Logo } from '../../components/images';
import { 
  FaCheckCircle, 
  FaClock, 
  FaExclamationTriangle,
  FaEnvelope,
  FaPhone,
  FaHome,
  FaEye
} from 'react-icons/fa';

const ApplicationSubmitted = () => {
  const navigate = useNavigate();
  const [user, loading, error] = useAuthState(auth);
  const [employerData, setEmployerData] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);
  const { logout } = EAuthData();

  // Load employer data
  useEffect(() => {
    const loadEmployerData = async () => {
      if (user) {
        try {
          const employerDoc = await getDoc(doc(db, 'employers', user.uid));
          if (employerDoc.exists()) {
            setEmployerData(employerDoc.data());
          }
        } catch (error) {
          console.error('Error loading employer data:', error);
        } finally {
          setDataLoading(false);
        }
      }
    };

    if (!loading && user) {
      loadEmployerData();
    } else if (!loading && !user) {
      navigate('/employer/login');
    }
  }, [user, loading, navigate]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <FaCheckCircle className="text-green-500 text-4xl" />;
      case 'submitted':
      case 'under_review':
        return <FaClock className="text-blue-500 text-4xl" />;
      case 'incomplete':
        return <FaExclamationTriangle className="text-yellow-500 text-4xl" />;
      default:
        return <FaClock className="text-gray-500 text-4xl" />;
    }
  };

  const getStatusMessage = (employerData) => {
    if (!employerData) return '';

    if (employerData.verificationStatus === 'approved' && employerData.isActive) {
      return {
        title: 'Application Approved!',
        message: 'Congratulations! Your employer account has been approved and activated. You can now access all platform features.',
        color: 'text-green-700',
        bgColor: 'bg-green-50 border-green-200'
      };
    }

    if (employerData.applicationStatus === 'submitted') {
      return {
        title: 'Application Under Review',
        message: 'Thank you for submitting your application and documents. Our team is currently reviewing your information. We will notify you via email once the review is complete.',
        color: 'text-blue-700',
        bgColor: 'bg-blue-50 border-blue-200'
      };
    }

    if (employerData.applicationStatus === 'incomplete') {
      return {
        title: 'Document Verification Required',
        message: 'Your basic registration is complete, but we need additional documents to verify your company. Please complete the document submission process.',
        color: 'text-yellow-700',
        bgColor: 'bg-yellow-50 border-yellow-200'
      };
    }

    return {
      title: 'Application Processing',
      message: 'Your application is being processed. Please check back later for updates.',
      color: 'text-gray-700',
      bgColor: 'bg-gray-50 border-gray-200'
    };
  };

  if (loading || dataLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-200 to-orange-500 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (!employerData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-200 to-orange-500 flex items-center justify-center">
        <div className="text-center text-white">
          <h2 className="text-2xl font-bold mb-4">No Application Found</h2>
          <p className="mb-6">We couldn't find your employer application.</p>
          <Link 
            to="/employer/signup" 
            className="bg-white text-orange-600 px-6 py-3 rounded-md font-medium hover:bg-gray-100"
          >
            Register Now
          </Link>
        </div>
      </div>
    );
  }

  const statusInfo = getStatusMessage(employerData);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-200 to-orange-500 p-4 md:p-10">
      {/* Header */}
      <div className="mb-8">
        <Link to="/">
          <div className='flex flex-row items-center mb-6 sm:mb-8'>
            <img src={Logo} className='w-8 h-8 sm:w-10 sm:h-10' alt="Logo"/>
            <p className='text-lg sm:text-xl lg:text-2xl ml-2 font-medium text-gray-800'>Oodles Internships</p>
          </div>
        </Link>
      </div>

      <div className="max-w-4xl mx-auto">
        {/* Main Status Card */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <div className="text-center mb-8">
            {getStatusIcon(employerData.verificationStatus)}
            <h2 className="text-3xl font-bold text-gray-800 mt-4 mb-2">
              Welcome, {employerData.companyName}!
            </h2>
          </div>

          {/* Status Information */}
          <div className={`border rounded-lg p-6 mb-8 ${statusInfo.bgColor}`}>
            <h3 className={`text-xl font-semibold mb-3 ${statusInfo.color}`}>
              {statusInfo.title}
            </h3>
            <p className={`${statusInfo.color} leading-relaxed`}>
              {statusInfo.message}
            </p>
          </div>

          {/* Application Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="font-semibold text-gray-700 mb-1">Application Status</div>
              <div className={`font-bold text-lg ${
                employerData.applicationStatus === 'submitted' ? 'text-blue-600' :
                employerData.applicationStatus === 'incomplete' ? 'text-yellow-600' :
                'text-gray-600'
              }`}>
                {employerData.applicationStatus.replace('_', ' ').toUpperCase()}
              </div>
            </div>

            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="font-semibold text-gray-700 mb-1">Verification Status</div>
              <div className={`font-bold text-lg ${
                employerData.verificationStatus === 'approved' ? 'text-green-600' :
                employerData.verificationStatus === 'pending' ? 'text-yellow-600' :
                'text-gray-600'
              }`}>
                {employerData.verificationStatus.toUpperCase()}
              </div>
            </div>

            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="font-semibold text-gray-700 mb-1">Account Status</div>
              <div className={`font-bold text-lg ${
                employerData.isActive ? 'text-green-600' : 'text-yellow-600'
              }`}>
                {employerData.isActive ? 'ACTIVE' : 'PENDING'}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-center space-x-4 mb-8">
            {employerData.applicationStatus === 'incomplete' && (
              <button
                onClick={() => navigate('/employer/document-verification')}
                className="bg-orange-500 text-white px-6 py-3 rounded-md font-medium hover:bg-orange-600 flex items-center"
              >
                <FaEye className="mr-2" />
                Complete Document Verification
              </button>
            )}

            {employerData.verificationStatus === 'approved' && employerData.isActive && (
              <button
                onClick={() => navigate('/employer/')}
                className="bg-green-500 text-white px-6 py-3 rounded-md font-medium hover:bg-green-600 flex items-center"
              >
                <FaHome className="mr-2" />
                Go to Dashboard
              </button>
            )}

            <button
              onClick={() => window.location.reload()}
              className="bg-gray-500 text-white px-6 py-3 rounded-md font-medium hover:bg-gray-600"
            >
              Refresh Status
            </button>
          </div>

          {/* Timeline */}
          <div className="border-t pt-8">
            <h4 className="text-lg font-semibold text-gray-800 mb-4">Application Timeline</h4>
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <div>
                  <div className="font-medium">Registration Completed</div>
                  <div className="text-sm text-gray-600">Basic company information submitted</div>
                </div>
              </div>

              {employerData.applicationStatus === 'submitted' && (
                <div className="flex items-center space-x-4">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <div>
                    <div className="font-medium">Documents Submitted</div>
                    <div className="text-sm text-gray-600">Required documents provided for verification</div>
                  </div>
                </div>
              )}

              <div className="flex items-center space-x-4">
                <div className={`w-3 h-3 rounded-full ${
                  employerData.verificationStatus === 'approved' ? 'bg-green-500' : 'bg-gray-300'
                }`}></div>
                <div>
                  <div className="font-medium">Verification & Approval</div>
                  <div className="text-sm text-gray-600">
                    {employerData.verificationStatus === 'approved' 
                      ? 'Application approved and account activated'
                      : 'Pending verification review'
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h4 className="text-lg font-semibold text-gray-800 mb-4">Need Help?</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="flex items-center space-x-3">
              <FaEnvelope className="text-orange-500" />
              <div>
                <div className="font-medium">Email Support</div>
                <a href="mailto:info.oodlestech@gmail.com" className="text-orange-600 hover:underline">
                  info.oodlestech@gmail.com
                </a>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <FaPhone className="text-orange-500" />
              <div>
                <div className="font-medium">Phone Support</div>
                <a href="tel:+1234567890" className="text-orange-600 hover:underline">
                  {/*+1 (234) 567-890*/}
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <div className="flex justify-center items-center space-x-4">
            <button
              onClick={async () => {
                try {
                  await logout();
                  navigate('/employer/login');
                } catch (error) {
                  console.error('Logout error:', error);
                  // Fallback: just navigate to login
                  navigate('/employer/login');
                }
              }}
              className="bg-gray-500 text-white px-6 py-2 rounded-md font-medium hover:bg-gray-600 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Named export for index.js
export { ApplicationSubmitted };

// Default export
export default ApplicationSubmitted;